import { useEffect } from 'react';

import { getRouteApi } from '@tanstack/react-router';

import type { PersonV1CurrentResponse } from 'client/types.gen';
import { isPersonExecutive } from 'helpers/person';

export const useDebouncedEffect = (callback: VoidFunction, delay = 300) => {
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    // Don't use timeout when delay is 0. Doing so would delay the callback
    // execution until the next tick as timeouts always run asynchronously.
    // This may lead to bugs where code depends on this effect running
    // immediately after its dependencies change.
    if (!delay) {
      callback();
    } else {
      timeoutId = setTimeout(() => {
        callback();
      }, delay);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [callback, delay]);
};

export const isPersonAdmin = (person?: PersonV1CurrentResponse): boolean =>
  Boolean((person?.roles ?? []).find((role) => role.title === 'Banff Admin'));

const privateRoute = getRouteApi('/_private');

/**
 * Whether the signed-in user is a Banff Admin. Reads the `/_private` route
 * context, so it is only valid in components rendered under that tree.
 */
export const useIsPersonAdmin = (): boolean => {
  const { user } = privateRoute.useRouteContext();
  return isPersonAdmin(user);
};

/** Admins may view any user; executives may only view their own `/users/$userId/*` area. */
export const canAccessUsersUserIdRoutes = (
  person: PersonV1CurrentResponse | undefined,
  userIdParam: string,
): boolean =>
  isPersonAdmin(person) ||
  (isPersonExecutive(person) && person?.uuid === userIdParam);

// NOTE: never gate on a roles-based "Executive Client" title check here:
// people/current strips the EXECUTIVE role from `roles` and exposes
// `is_executive` instead — use isPersonExecutive from helpers/person.
